import Link from "next/link"
import { useTranslations } from "next-intl"
import { DeferredLanguageSelector } from "@/shared/components/i18n/deferred-language-selector"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { Logo } from "@/shared/components/logo"
import { Separator } from "@/shared/components/ui/separator"

const footerLinks = {
  product: {
    titleKey: "product",
    links: [
      { labelKey: "explore", href: "/explore" },
      { labelKey: "chat", href: "/chat" },
      { labelKey: "create", href: "/create" },
      { labelKey: "aiImageGenerator", href: "/generate/image" },
      { labelKey: "subscription", href: "/subscription" },
    ],
  },
  company: {
    titleKey: "company",
    links: [
      { labelKey: "blog", href: "/blog" },
      { labelKey: "affiliateProgram", href: "/affiliate" },
      { labelKey: "pricing", href: "/pricing" },
    ],
  },
  legal: {
    titleKey: "legal",
    links: [
      { labelKey: "legalInformation", href: "/legal" },
      { labelKey: "termsOfService", href: "/legal/terms-of-service" },
      { labelKey: "privacyPolicy", href: "/legal/privacy-policy" },
      { labelKey: "dmca", href: "/legal/dmca-policy" },
      { labelKey: "affiliateTerms", href: "/legal/affiliate-terms" },
      { labelKey: "reportIntimateImage", href: "/legal/report-intimate-image" },
    ],
  },
  connect: {
    titleKey: "connect",
    links: [
      { labelKey: "xTwitter", href: "https://x.com/pleasurai" },
      { labelKey: "instagram", href: "https://instagram.com/pleasur.ai" },
    ],
  },
} as const

export function Footer() {
  const t = useTranslations("footer")
  const currentYear = new Date().getFullYear()

  return (
    <footer className="border-t border-border bg-sidebar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top section: Logo + tagline */}
        <div className="py-12">
          <div className="mb-8">
            <Logo size="md" responsive />
            <p className="mt-3 max-w-md text-sm text-muted-foreground">
              {t("tagline")}
            </p>
            <p data-testid="ai-disclosure-site" className="mt-2 max-w-md text-xs text-muted-foreground">
              {t("aiDisclosure")} {t("minorsNotice")}
            </p>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {Object.values(footerLinks).map((section) => (
              <div key={section.titleKey}>
                <h3 className="text-xs font-medium uppercase tracking-widest text-foreground/80 mb-4">
                  {t(`sections.${section.titleKey}`)}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.labelKey}>
                      {link.href.startsWith("http") ? (
                        <Link
                          href={link.href}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t(`links.${link.labelKey}`)}
                        </Link>
                      ) : (
                        <LocalizedLink
                          href={link.href}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {t(`links.${link.labelKey}`)}
                        </LocalizedLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Bottom section: Copyright */}
        <div className="flex flex-col items-center justify-between gap-4 py-6 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            {t("copyright", { year: currentYear })}
          </p>
          <DeferredLanguageSelector />
          <p className="text-xs text-muted-foreground">
            {t("madeWithCare")}
          </p>
        </div>
      </div>
    </footer>
  )
}
